import React, { useEffect, useRef } from 'react';
import { initializeCesium, loadAndZoomToAsset, removeAssetById, flyToPosition } from '../cesium';

interface CesiumProps {
  bimAssetIds?: number[];
  mapStyle?: string;
  onCesiumCameraMove?: (settings: any) => void;
  cesiumCameraPosition?: { center: [number, number]; height: number; bearing?: number; pitch?: number };
  shouldFlyTo?: { center: [number, number]; height: number; bearing?: number; pitch?: number };
  onReady?: () => void;
  [key: string]: any; // For other props that might be passed
}

// Helper function to convert camera position format
function convertCameraPosition(position?: { center: [number, number]; height: number; bearing?: number; pitch?: number }) {
  if (!position) return undefined;
  
  return {
    center: position.center,
    height: position.height,
    bearing: position.bearing || 0,
    pitch: position.pitch || -45
  };
}

function Cesium({ 
  bimAssetIds, 
  onCesiumCameraMove, 
  cesiumCameraPosition, 
  shouldFlyTo, 
  onReady, 
  ...otherProps 
}: CesiumProps) {
  const initialized = useRef(false);
  const prevAssetIdsRef = useRef<number[]>([]);
  const cesiumCameraPositionRef = useRef(cesiumCameraPosition);
  const lastShouldFlyToRef = useRef<any>(null);

  // Update the ref when cesiumCameraPosition changes
  useEffect(() => {
    cesiumCameraPositionRef.current = cesiumCameraPosition;
  }, [cesiumCameraPosition]);

  useEffect(() => {
    const init = async () => {
      if (!initialized.current) {
        initialized.current = true; // Set flag early
        
        const hasInitialCameraPosition = !!cesiumCameraPosition;
        
        await initializeCesium({
          assetIds: bimAssetIds,
          initialCameraPosition: convertCameraPosition(cesiumCameraPosition),
          autoZoomToAssets: !hasInitialCameraPosition, // Don't auto-zoom if we have a camera position
          onObjectClick: (attributes) => {
            window.dispatchEvent(new CustomEvent('cesiumObjectClick', { detail: { attributes } }));
            window.dispatchEvent(new CustomEvent('objectClick', { detail: { attributes } }));
          },
          onCameraMove: onCesiumCameraMove,
          onReady: onReady,
        });

        prevAssetIdsRef.current = bimAssetIds || [];
      }
    };
    init();
  }, []); // Run only once on mount

  // Handle shouldFlyTo changes (camera transitions)
  useEffect(() => {
    if (!initialized.current || !shouldFlyTo) return;
    
    // Check if shouldFlyTo actually changed (not just a re-render)
    if (lastShouldFlyToRef.current && 
        JSON.stringify(lastShouldFlyToRef.current) === JSON.stringify(shouldFlyTo)) {
      return; // Same value, don't trigger again
    }
    
    // Convert positions to the expected format and trigger the camera transition
    const flyToSettings = convertCameraPosition(shouldFlyTo);
    const currentCameraPosition = convertCameraPosition(cesiumCameraPositionRef.current);
    
    // Update the ref to track this shouldFlyTo
    lastShouldFlyToRef.current = shouldFlyTo;
    
    // Trigger the camera transition
    if (flyToSettings) {
      flyToPosition(flyToSettings, currentCameraPosition);
    }
  }, [shouldFlyTo]); // Only depend on shouldFlyTo, not cesiumCameraPosition

  useEffect(() => {
    if (!initialized.current) return; // Don't run if initialization hasn't started

    const currentIds = bimAssetIds || [];
    const previousIds = prevAssetIdsRef.current || [];
    
    const newAssetIds = currentIds.filter(id => !previousIds.includes(id));
    if (newAssetIds.length > 0) {
      for (const assetId of newAssetIds) {
        loadAndZoomToAsset(assetId); // Uses default 2s flight time
      }
    }

    const removedAssetIds = previousIds.filter(id => !currentIds.includes(id));
    if (removedAssetIds.length > 0) {
      for (const assetId of removedAssetIds) {
        removeAssetById(assetId);
      }
    }
    
    prevAssetIdsRef.current = currentIds;
  }, [bimAssetIds]);

  return <div id="cesiumContainer" className="w-full h-full" />;
}

export default Cesium; 